<template>
  <section class="project3d-page">
    <div class="container">
      <div class="project3d__breadcrumbs">
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <h1
        v-if="project3d"
        class="project3d__title h2"
      >
        {{ project3d.title }}
      </h1>

      <template v-if="project3d">
        <div
          v-if="project3d"
          class="project3d__content"
        >
          <div class="--left">
            <template v-if="isVisibleSliderAllCollection">
              <client-only>
                <SliderAllCollection :project="project3d"/>
                <div class="--space" />
              </client-only>
            </template>

            <client-only>
              <Share />
            </client-only>

            <div class="--space" />

            <OtherProjects :project-id="project3d.id" />
          </div>
          <div class="--right cms-block-stub">
            <template v-if="project3d.text && Array.isArray(project3d.text)">
              <div
                v-for="(textItem, index) in project3d.text"
                :key="`project-3d-textItem-${index}`"
                class="text"
              >
                <div v-if="textItem.html" v-html="textItem.html" />
                <div
                  class="images-wrapper"
                  v-if="textItem.image_id"
                >
                  <nuxt-img
                    :src="`${textItem.image_id}`"
                    alt="img"
                    width="850"
                    height="638"
                  />
                </div>
              </div>
            </template>
            <template v-else-if="project3d.text && !Array.isArray(project3d.text)">
              <div
                class="text"
                v-html="project3d.text"
              />
            </template>
            <div class="images-wrapper">
              <template v-if="project3d">
                <template v-if="false">
                  <NuxtImg
                    v-if="project3d?.image_id"
                    :src="String(project3d?.image_id)"
                    alt="img"
                    width="850"
                    height="638"
                fit="default"
                placeholder
              />
              </template>
                <div v-for="image in project3d.images" :key="image.imageId">

                <NuxtImg
                  :src="String(image.imageId)"
                  alt="img"
                  width="850"
                  height="638"
                    fit="default"
                    placeholder
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <cms-page
      v-if="Boolean(project3d && cmsPageIdAfterContent)"
      :page-id="cmsPageIdAfterContent"
      content-only
    />

    <template v-if="project3d && !$viewport.isGreaterOrEquals('lg')">
      <OtherProjects
        :project-id="project3d.id"
      />
    </template>
  </section>
</template>

<script setup lang="ts">
import Breadcrumbs from '~/components/UI/Breadcrumbs.vue';
import Share from '~/components/articles/Share.vue';
import type { Project3dListType } from '~/components/collection/3d-project/Project3d.vue';
import OtherProjects from '~/components/collection/3d-project/OtherProjects.vue';
import { useRequestURL } from '#imports';
import { appConfigStore as useAppConfigStore } from '#sitis/stores/modules/app-store';
import type { ComputedRef } from 'vue';
import SliderAllCollection from "~/components/three-d-projects/SliderAllCollection.vue";

const $route = useRoute();
const {
  $api,
  $viewport
} = useNuxtApp();
const appConfigStore = useAppConfigStore();

const { data: project3d, execute, error } = useLazyAsyncData<Project3dListType>($route.path, async () => await $api.agent
  .get(`projects/${$route.params.slug[0]}?expand=products`)
  .then((res) => res._data), {
  immediate: false
});
await getProjectsList();

const breadcrumbs = computed(() => [
  { title: 'Главная', path: '/' },
  { title: 'Список 3D проектов', path: '/3d-projects' },
  { title: `${project3d.value?.title}`, path: `${project3d.value?.url}` }
]);

async function getProjectsList () {
  await execute();

  if (project3d.value?.url !== $route.fullPath) {
    throw createError({
      statusCode: 404,
      statusMessage: '3D-проект<br/>не найден'
    });
  }
}

const url = useRequestURL();
const productImage = useImage();
const { setSEOTitles } = useSitisCMSPage();

const project3dDescription = computed(() => {
  if (Array.isArray(project3d.value?.text)) {
    const htmlArr = project3d.value?.text.map((item) => {
      return item?.html
    })
    return htmlArr ? htmlArr.join('') : ''
  } else return project3d.value?.text
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  name: project3d.value?.title || 'title',
  description: project3dDescription.value || '',
  url: url.href,
  image: productImage(String(project3d.value?.image_id))
}));

setSEOTitles({
  seoTitle: project3d.value?.meta_title || project3d.value?.title || '',
  seoDescription: project3d.value?.meta_description || project3dDescription.value || '',
  seoKeywords: project3d.value?.meta_keywords || ''
});

const isVisibleSliderAllCollection: ComputedRef<boolean> = computed(() => {
  return Boolean((project3d.value?.products || []).length > 0)
});

const cmsPageIdAfterContent: ComputedRef<number | undefined> = computed(() => {
  const pageId = appConfigStore?.vars?.['3D-проект контент под данными (ID CMS страницы)'] || '';
  if (!pageId) {
    return undefined
  }
  return Number.parseFloat(pageId)
});
</script>

<style scoped lang="scss">
.project3d__title {
	margin-bottom: 64px;
	margin-top: 16px;
}

.project3d__content {
	display: flex;

	& .images-wrapper {
		display: flex;
		flex-direction: column;
		gap: 16px;

		& img {
			border-radius: 10px;
			aspect-ratio: 850 / 638;
			object-fit: cover;
		}
	}

	& .text {
		margin-bottom: 16px;

    & > :last-child {
      padding-bottom: 0; // чтобы не наслаивать отступы
      margin-bottom: 0;
    }
	}

  .project3d-page__text-with-images {
    margin-bottom: 16px;
  }

	.--left {
		width: 100%;
		max-width: 320px;
		min-height: 280px;
		margin-right: 113px;

		.--space {
			margin-top: 38px;
		}
	}

	.--right {
		flex: 1;
		max-width: 850px;

    &.cms-block-stub {
      margin-top: 0;
      /** v-deep - дирректива для переопределения стиля на глобальный */
      ::v-deep & > .text > p:first-child {
        margin-top: 0;
      }
    }
	}

	h5.title-block {
		color: #989898;
	}

	h3.title-block {
		font-weight: 600;
	}

	.accordion-gallery-block {
		padding: 0;
	}

	.cms-block-image-images {
		width: 100%;
		max-width: initial;
		border-radius: 30px;
		position: relative;
		overflow: hidden;

		img {
			width: 100%;
		}
	}
}

@media (max-width: 1439px) {
	.project3d__content {
		.--left {
			margin-right: 64px;
		}

		.--right {
			max-width: 796px;
		}
	}
}

@media (max-width: 1359px) {
	.project3d__content {
		.--left {
			margin-right: 32px;
		}

		.--right {
			max-width: 640px;
		}
	}
}

@media (max-width: 1023px) {
	.project3d {
		padding-top: 33px;
	}
	.project3d__breadcrumbs {
		display: none;
	}
	.project3d__title {
    margin-top: 32px;
		margin-bottom: 24px;
	}
	.project3d__image {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: initial;
		margin: 0;
	}
	.project3d__container-image {
		display: flex;
		margin: 0 -20px;
		max-width: calc(100% + 40px);
		border-radius: 24px;
		position: relative;
		overflow: hidden;
		margin-bottom: 23px;

		&:after {
			content: "";
			float: left;
			padding-top: 61%;
		}
	}
	.project3d__content {
		flex-direction: column-reverse;

		.cms-block-stub p {
			line-height: 150%;
		}

		.--left {
			margin-top: 61px;
		}

		.--left .--space {
			margin-top: 49px;
		}

    .--left .other-news {
      display: none;
    }

		.--right {
			max-width: initial;
		}
	}
}
</style>
